import React, { useState, useEffect, useRef, useCallback } from 'react';
import authAxios from '../services/Api';
import logger from '../utils/logger';
import './IframeViewer.css';

/**
 * IframeViewer Component with postMessage Token Support
 *
 * Securely passes authentication token to embedded application using postMessage API
 * instead of URL parameters for better security.
 *
 * @param {string} url - The base URL to load in the iframe
 * @param {function} onBack - Callback function when back button is clicked
 * @param {string} title - Title to display in the header
 * @param {string} token - Authentication token to pass via postMessage
 */
const IframeViewer = ({ url, onBack, title = 'External App', token, refreshToken }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [tokenSent, setTokenSent] = useState(false);
  const iframeRef = useRef(null);
  // First-handshake-done flag. After the embedded app acks once, any further
  // *_READY message is treated as a refresh request and we fetch a fresh token
  // from the backend instead of re-sending the (possibly expired) prop value.
  const tokenSentRef = useRef(false);
  // Single-flight guard so concurrent _READY messages don't trigger
  // overlapping /current-token fetches.
  const sendingRef = useRef(false);

  // Send token to iframe — useCallback so it can be safely listed in effect deps
  const sendTokenToIframe = useCallback(async (forceFresh = false) => {
    if (sendingRef.current) return;
    if (!iframeRef.current?.contentWindow) return;
    sendingRef.current = true;

    try {
      let outgoingToken = token;
      let outgoingRefresh = refreshToken;

      if (forceFresh) {
        // Refresh path: backend reads the HttpOnly accessToken cookie and
        // returns a fresh embed token. If FlowForms' own session has expired
        // this will 401 and authAxios will redirect to login — which is the
        // correct behavior.
        try {
          const response = await authAxios.get('/api/v1/user/current-token');
          outgoingToken = response.data?.accessToken ?? token;
          outgoingRefresh = response.data?.refreshToken ?? refreshToken;
        } catch (err) {
          logger.error('Failed to fetch fresh embed token:', err);
          return;
        }
      }

      if (!outgoingToken) return;

      const targetOrigin = new URL(iframeRef.current.src).origin;
      iframeRef.current.contentWindow.postMessage(
        { type: 'FLOWFORM_TOKEN', token: outgoingToken, ...(outgoingRefresh && { refreshToken: outgoingRefresh }) },
        targetOrigin
      );
    } catch (error) {
      logger.error('Failed to send token to iframe:', error);
    } finally {
      sendingRef.current = false;
    }
  }, [token, refreshToken]);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.type === 'APIBUILDER_READY' || event.data?.type === 'PDFBUILDER_READY') {
        // First _READY = initial auth (use prop token). Subsequent _READY =
        // the embedded app's token expired and is asking for a refresh.
        sendTokenToIframe(tokenSentRef.current);
      }

      if (event.data?.type === 'FLOWFORM_TOKEN_RECEIVED') {
        if (event.data.success) {
          tokenSentRef.current = true;
          setTokenSent(true);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendTokenToIframe]);

  // Handle iframe load — fallback send if APIBUILDER_READY signal was missed
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    // Attempt after short delay in case ready signal fires just after load
    const t1 = setTimeout(() => sendTokenToIframe(), 500);
    // Final fallback at 1 second
    const t2 = setTimeout(() => sendTokenToIframe(), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [sendTokenToIframe]);

  // Handle iframe error
  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
    logger.error('Iframe failed to load');
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onBack();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [onBack]);

  // Reload iframe
  const reloadIframe = () => {
    setIsLoading(true);
    setHasError(false);
    setTokenSent(false);
    tokenSentRef.current = false;
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  return (
    <div className="iframe-viewer-container">
      {/* Header with back button */}
      <div className="iframe-viewer-header">
        <button
          className="back-button"
          onClick={onBack}
          title="Back to Dashboard (ESC)"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <span>Back to Dashboard</span>
        </button>

        <h2 className="iframe-viewer-title">
          {title}
          {tokenSent && (
            <span className="token-status" title="Token received by API Builder">
              🔒
            </span>
          )}
        </h2>

        {/* <div className="iframe-viewer-actions">
          <button
            className="reload-button"
            onClick={reloadIframe}
            title="Reload"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </button>
        </div> */}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="iframe-viewer-loading">
          <div className="loading-spinner"></div>
          <p>Loading {title}...</p>
          {token && !tokenSent && (
            <p className="loading-subtext">Authenticating...</p>
          )}
        </div>
      )}

      {/* Error message */}
      {hasError && (
        <div className="iframe-viewer-error">
          <div className="error-content">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3>Unable to Load Application</h3>
            <p>The application at <code>{url}</code> could not be loaded.</p>
            <p>Please ensure the application is running and try again.</p>
            <button className="retry-button" onClick={reloadIframe}>
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        id="embedded-app-iframe"
        src={url}
        className="iframe-viewer-frame"
        title={title}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        allow="clipboard-read; clipboard-write"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  );
};

export default IframeViewer;
